import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { ApiHelper } from "../../utils/ApiHelper";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import {
    GET_GROUP_MESSAGES,
    GROUP_CHAT_DETAIL,
    GET_MONGO_USER_ID,
    GROUP_CHAT_UPLOAD_URL,
    GET_GROUP_FILES,
    UPDATE_GROUP_STATUS,
    UPDATE_MILESTONE,
    CREATE_FEEDBACK,
} from "../../lib/API_KEYS";

import RFQUpload from "./RFQUpload";
import DeliveryHistory from "./DeliveryHistory";
import MessageList from "./MessageList";
import MessageBox from "./MessageBox";
import ProposalCard from "./ProposalCard";
import { APP_CONFIG } from "../../config/APP_CONFIG";
import InitiateProjectModal from "./InitiateProjectModal";
import AppointProjectManagerModal from "./AppointProjectManagerModal";
import MilestoneModal from "./MilestoneModal";
import MilestonePaymentModal from "./MilestonePaymentModal";
import BuyerReviewModal from "./BuyerReviewModal";
import RaiseFlagModal from "./RaiseFlagModal";
import { Auth } from "aws-amplify";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { buyerRoles, sellerRoles } from "../../config/ROLES";

const Communications = ({ id, userRole }) => {
    const { setCommunicationsTitle } = useBreadcrumb();
    const [deliveryVisible, setDeliveryVisible] = useState(true);
    const [messages, setMessages] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [message, setMessage] = useState("");
    const [currentUser, setCurrentUser] = useState(null);
    const [groupFiles, setGroupFiles] = useState([]);
    const [proposalFile, setProposalFile] = useState(null);
    const [showInitiateProjectModal, setShowInitiateProjectModal] = useState(false);
    const [paymentValue, setPaymentValue] = useState("");
    const [showProjectManagerModal, setShowProjectManagerModal] = useState(false);
    const [selectedPM, setSelectedPM] = useState(null);
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [showMilestonePaymentModal, setShowMilestonePaymentModal] = useState(false);
    const [milestone, setMilestone] = useState({})
    const [showBuyerReviewModal, setShowBuyerReviewModal] = useState(false);
    const [showRaiseFlagModal, setShowRaiseFlagModal] = useState(false);
    const socketRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Auto scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Get Mongo user ID via Cognito
    useEffect(() => {
        const fetchMongoUserId = async () => {
            try {
                const user = await Auth.currentAuthenticatedUser();
                const cognitoSub = user.attributes.sub;

                const res = await new ApiHelper().FetchFromServer(
                    GET_MONGO_USER_ID[0].replace(":cognitoId", cognitoSub),
                    GET_MONGO_USER_ID[1]
                );
                if (!res?.isError) setCurrentUser(res.data);
            } catch (error) {
                console.error("Error fetching Mongo user ID:", error);
            }
        };

        fetchMongoUserId();
    }, []);

    // Fetch group details
    const fetchGroup = async () => {
        const res = await new ApiHelper().FetchFromServer(
            GROUP_CHAT_DETAIL[0].replace(":groupId", id),
            GROUP_CHAT_DETAIL[1]
        );
        console.log("Fetched group details:", res);
        if (res?.statusCode === 404 || res?.isError) {
            toast.error("You do not have permission to view this chat");
            return;
        }
        if (!res?.isError) {
            setSelectedGroup(res.data);

            // Calculate displayId for breadcrumb (role-aware)
            const group = res.data;
            const rfqIdString = group.requirementId?.requirementId || null;
            let displayId;

            if (userRole === 'Admin') {
                // Admin sees 3 phases: RFQ → CRT → PRG
                if (group.statusOrder < 5) {
                    displayId = rfqIdString || group.groupId;
                } else if (group.statusOrder < 9) {
                    displayId = group.groupId.replace(/^PRG-/, 'CRT-');
                } else {
                    displayId = group.groupId;
                }
            } else {
                // Buyer/Seller/PM see 2 phases only: RFQ → PRG (skip CRT)
                if (group.statusOrder < 9) {
                    // Pre-project phase: show as RFQ
                    displayId = rfqIdString || group.groupId;
                } else {
                    // Project phase: show as PRG
                    displayId = group.groupId;
                }
            }

            setCommunicationsTitle(displayId);
        }
    };
    useEffect(() => {
        fetchGroup();

        // Clear title on unmount
        return () => setCommunicationsTitle(null);
    }, [id]);

    // Initialize socket connection when group selected
    useEffect(() => {
        if (!selectedGroup) return;

        const socket = io(APP_CONFIG.SOCKET_URL, {
            transports: ["websocket"],
            withCredentials: true,
        });

        socketRef.current = socket;
        socket.emit("joinGroup", selectedGroup._id);

        socket.on("connect", () => console.log("🟢 Connected:", socket.id));
        socket.on("disconnect", () => console.log("🔴 Disconnected"));

        socket.on("newMessage", (msg) => setMessages((prev) => [...prev, msg]));

        // Listen for system status update notifications
        socket.on("statusUpdate", (notification) => {
            console.log("📢 Status Update:", notification);
            setMessages((prev) => [...prev, notification]);
            // Also refresh the group to update timeline
            fetchGroup();
        });

        return () => socket.disconnect();
    }, [selectedGroup]);

    // Fetch old messages
    useEffect(() => {
        const fetchMessages = async () => {
            if (!selectedGroup?._id) return;
            const res = await new ApiHelper().FetchFromServer(
                GET_GROUP_MESSAGES[0].replace(":groupId", selectedGroup._id),
                GET_GROUP_MESSAGES[1]
            );
            if (!res?.isError) setMessages(res.data || []);
        };
        fetchMessages();
    }, [selectedGroup]);

    //Fetch group files
    const fetchFiles = async () => {
        const res = await new ApiHelper().FetchFromServer(
            GET_GROUP_FILES[0].replace(":groupId", selectedGroup._id),
            GET_GROUP_FILES[1]
        );

        if (!res?.isError) setGroupFiles(res.data || []);
        const proposalFile = res.data.find((file) => file.status === 'Proposal Sent');
        if (proposalFile) setProposalFile(proposalFile);
    };

    useEffect(() => {
        if (!selectedGroup?._id) return;

        fetchFiles();
    }, [selectedGroup]);

    // Send message
    const handleSend = () => {
        if (!message.trim()) return;
        socketRef.current.emit("sendMessage", {
            groupId: selectedGroup._id,
            message,
            senderId: currentUser?._id,
            senderName: currentUser?.name,
            senderEmail: currentUser?.email,
            senderCompanyId: currentUser?.companyId,
        });
        setMessage("");
    };

    //File upload handler — contains upload + socket emit + DB save
    const getFileUploadUrl = async (file) => {
        try {
            if (!file) return;
            const fileType = file.type || "link";
            const fileSize = file.size || 0;
            let uploadedFileUrl = "";

            // If pasted link (no actual file)
            // if (file.isLink) {
            //     uploadedFileUrl = file.name;
            // } else {
            // Request upload URL
            const uploadRes = await new ApiHelper().FetchFromServer(
                ...GROUP_CHAT_UPLOAD_URL,
                undefined,
                {
                    fileName: file.name,
                    fileType,
                    fileSize,
                    groupId: selectedGroup._id,
                }
            );

            if (uploadRes.data?.isError || !uploadRes.data?.signedRequest) {
                console.error("Failed to get upload URL");
                return;
            }

            // Upload to S3
            await fetch(uploadRes.data.signedRequest, {
                method: "PUT", body: file, headers: {
                    "Content-Type": file.type,
                },
            });
            uploadedFileUrl = uploadRes.data.fileUrl;
            // }
            return uploadedFileUrl
        } catch (err) {
            console.error("Upload failed:", err);
        }
    }
    const handleFileUpload = async (file, description, RFQUpload, link) => {
        try {
            if (!file && !link) return;
            let uploadedFileUrl = link ? link : await getFileUploadUrl(file)
            const payload = {
                groupId: selectedGroup._id,
                senderId: currentUser?._id,
                senderName: currentUser?.name,
                senderEmail: currentUser?.email,
                senderCompanyId: currentUser?.companyId,
                fileUrl: uploadedFileUrl,
                fileName: file?.name || link,
                fileType: file?.type || "link",
                fileSize: file?.size || 0,
                description,
            }
            if (userRole == 'Seller' && RFQUpload) {
                if (selectedGroup.status === 'RFQ Sent' || selectedGroup.status === 'Proposal Declined') { payload.status = 'Proposal Sent'; payload.statusOrder = 3; };
                if (description === 'Milestones Completed') await updateMilestoneStatus(description)
            } else if (userRole == 'Buyer' && RFQUpload) {
                if (selectedGroup.status === "NDA Signed") { payload.status = 'RFQ Sent'; payload.statusOrder = 2; }
                if (selectedGroup.status === 'Proposal Accepted') { payload.status = 'Purchase Order Sent'; payload.statusOrder = 5; }
            }
            socketRef.current.emit(
                "fileUploaded",
                payload
            );
            // if (RFQUpload) setGroupFiles((prev) => [...prev, payload]);
            if (RFQUpload) { fetchFiles(); fetchGroup(); }
        } catch (err) {
            console.error("Upload failed:", err);
        }
    };

    const handleGroupStatusUpdate = async (status, statusOrder) => {
        if (!selectedGroup?._id) return;

        const res = await new ApiHelper().FetchFromServer(
            UPDATE_GROUP_STATUS[0].replace(":groupId", selectedGroup._id),
            UPDATE_GROUP_STATUS[1],
            undefined,
            { status, statusOrder }
        );

        if (!res?.isError) {
            fetchGroup();
        }
    };

    const Signing3PartAgreement = async (status, statusOrder, paymentValue) => {
        console.log("Tripartite Agreement Initiated");
        if (!paymentValue) {
            toast.error("Please provide a Payment Value!");
            return;
        }

        try {
            const res = await new ApiHelper().FetchFromServer(
                UPDATE_GROUP_STATUS[0].replace(":groupId", selectedGroup._id),
                UPDATE_GROUP_STATUS[1],
                undefined,
                { status, statusOrder, paymentValue }
            );
            toast.success(`Status updated to "${status}"!`);
            setShowInitiateProjectModal(false);
            setPaymentValue("");
            if (!res?.isError) {
                // Update local state or perform any other actions
                fetchGroup();
            }
        } catch (err) {
            console.error("Error updating status:", err);
            toast.error("Failed to update status. Please try again.");
        }
    };

    const handleAssignProjectManager = async (status, statusOrder, selectedPM) => {
        if (!selectedGroup?._id) return;

        const res = await new ApiHelper().FetchFromServer(
            UPDATE_GROUP_STATUS[0].replace(":groupId", selectedGroup._id),
            UPDATE_GROUP_STATUS[1],
            undefined,
            { status, statusOrder, selectedPM }
        );
        setShowProjectManagerModal(false);

        if (!res?.isError) {
            // Update local state or perform any other actions
            fetchGroup();
        }
    };

    const handleAddMilestone = async (status, statusOrder, milestones) => {
        if (!selectedGroup?._id) return;

        const res = await new ApiHelper().FetchFromServer(
            UPDATE_GROUP_STATUS[0].replace(":groupId", selectedGroup._id),
            UPDATE_GROUP_STATUS[1],
            undefined,
            { status, statusOrder, milestones }
        );
        setShowMilestoneModal(false);

        if (!res?.isError) {
            fetchGroup();
        }
    };

    const updateMilestoneStatus = async (status) => {
        return new ApiHelper().FetchFromServer(
            UPDATE_MILESTONE[0].replace(":groupId", selectedGroup._id).replace(":milestoneId", milestone?.id),
            UPDATE_MILESTONE[1],
            undefined,
            { status }
        );
    }

    const handleMilestoneStatusUpdate = async (status) => {
        if (!selectedGroup?._id) return;
        const res = await updateMilestoneStatus(status)
        setShowMilestonePaymentModal(false)
        if (!res?.isError) {
            fetchGroup();
        }
    };

    const handleFeedbackUpdate = async (feedbackData) => {
        const { _id: groupId, requirementId, sellerRequirementId, companyId, companyName, buyerCompanyId, buyerCompanyName, pmId, pmCompanyId } = selectedGroup || {};
        const { ratings, reviewText, feedback } = feedbackData || {};

        if (!groupId) return;

        const payload = {
            ...ratings,
            reviewText,
            groupId,
            requirementId,
            sellerRequirementId,
            companyId,
            companyName,
            buyerCompanyId,
            buyerCompanyName,
            feedback,
            pmId,
            pmCompanyId
        }

        const res = await new ApiHelper().FetchFromServer(
            ...CREATE_FEEDBACK,
            undefined,
            payload
        );

        await new ApiHelper().FetchFromServer(
            UPDATE_GROUP_STATUS[0].replace(":groupId", selectedGroup._id),
            UPDATE_GROUP_STATUS[1],
            undefined,
            { feedback }
        );

        if (!res?.isError) {
            fetchGroup();
            setShowBuyerReviewModal(false);
        }
    };

    const handleRaiseFlag = async (description) => {
        if (!selectedGroup?._id) return;

        try {
            const res = await new ApiHelper().FetchFromServer(
                `/group-chats/${selectedGroup._id}/raise-flag`,
                'POST',
                undefined,
                { description }
            );

            if (!res?.isError) {
                toast.success("Escalation sent to admin successfully!");
                setShowRaiseFlagModal(false);
            } else {
                const errorMessage = res.message || "Failed to send escalation to admin. Please try again or contact support.";
                toast.error(errorMessage);
                // Keep modal open so user can try again
            }
        } catch (error) {
            console.error("Error raising flag:", error);
            toast.error("Failed to send escalation due to a technical issue. Please contact support.");
            // Keep modal open
        }
    };

    return (
        <div
            className="w-100"
            style={{ background: "#f8f9fa", height: "90vh", overflow: "hidden" }}
        >
            <div className="d-flex h-100">
                <div className="d-flex flex-grow-1 overflow-hidden">
                    <div
                        className="border-end d-flex flex-column h-100"
                        style={{ width: deliveryVisible ? "70%" : "100%" }}
                    >
                        {selectedGroup ? (
                            <>
                                {((userRole === 'Seller' && selectedGroup.statusOrder === 2) ||
                                    (userRole === 'Buyer' && [1, 4].includes(selectedGroup.statusOrder))) &&
                                    (<div
                                        style={{
                                            flexShrink: 0,
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 10,
                                        }}
                                    >
                                        <RFQUpload
                                            onUpload={handleFileUpload}
                                            documentType={userRole === 'Seller' ? 'Proposal' :
                                                userRole === 'Buyer' && selectedGroup.statusOrder === 4 ? 'PO' : 'RFQ'}
                                        />
                                    </div>)}

                                {selectedGroup.statusOrder == 3 && userRole === 'Buyer' && (<div
                                    style={{
                                        flexShrink: 0,
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 10,
                                    }}
                                >
                                    <ProposalCard
                                        proposalFile={proposalFile}
                                        onAccept={() => handleGroupStatusUpdate('Proposal Accepted', 4)}
                                        onReject={() => handleGroupStatusUpdate('Proposal Declined', 2)}
                                    />
                                </div>)}

                                <div
                                    className="flex-grow-1 overflow-auto p-3 bg-white"
                                    style={{
                                        minHeight: 0, // prevents flex overflow
                                    }}
                                >
                                    <MessageList messages={messages} currentUser={currentUser} />
                                    <div ref={messagesEndRef} />
                                </div>

                                <div
                                    style={{
                                        flexShrink: 0,
                                        position: "sticky",
                                        bottom: 0,
                                        zIndex: 10,
                                    }}
                                >
                                    <MessageBox
                                        message={message}
                                        setMessage={setMessage}
                                        onSend={handleSend}
                                        onFileSelect={handleFileUpload}
                                        disabled={selectedGroup.statusOrder == 3.5}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <h6>Select a group to start chatting</h6>
                            </div>
                        )}
                    </div>


                    {deliveryVisible && (
                        <div className="d-flex flex-column h-100" style={{ width: "30%" }}>
                            <div className="flex-grow-1 overflow-auto p-3">
                                <DeliveryHistory
                                    userRole={userRole}
                                    selectedGroup={selectedGroup}
                                    files={groupFiles}
                                    onAccept={() => handleGroupStatusUpdate('Documents Ready', 6)}
                                    setShowInitiateProjectModal={setShowInitiateProjectModal}
                                    setShowProjectManagerModal={setShowProjectManagerModal}
                                    setShowMilestoneModal={setShowMilestoneModal}
                                    setShowMilestonePaymentModal={setShowMilestonePaymentModal}
                                    milestone={milestone}
                                    setMilestone={setMilestone}
                                    setShowBuyerReviewModal={setShowBuyerReviewModal}
                                    setShowRaiseFlagModal={setShowRaiseFlagModal}
                                />
                                {/* add admin in the group also  */}
                            </div>
                        </div>
                    )}

                    <InitiateProjectModal
                        show={showInitiateProjectModal}
                        onClose={() => setShowInitiateProjectModal(false)}
                        onSave={() => Signing3PartAgreement('Tripartite Agreement Initiated', 7, paymentValue)}
                        paymentValue={paymentValue}
                        setPaymentValue={setPaymentValue}
                    />

                    <AppointProjectManagerModal
                        show={showProjectManagerModal}
                        onClose={() => setShowProjectManagerModal(false)}
                        onSave={() => handleAssignProjectManager('Project Manager Assigned', 9, selectedPM)}
                        selectedPM={selectedPM}
                        setSelectedPM={setSelectedPM}
                    />
                    <MilestoneModal
                        show={showMilestoneModal}
                        onClose={() => setShowMilestoneModal(false)}
                        onSave={handleAddMilestone}
                        selectedGroup={selectedGroup}
                        getFileUploadUrl={getFileUploadUrl}
                        projectValue={selectedGroup?.paymentValue || 0}
                    />
                    <MilestonePaymentModal
                        show={showMilestonePaymentModal}
                        onClose={() => setShowMilestonePaymentModal(false)}
                        onSave={() => window.open(`${APP_CONFIG.ESCROW_URL}/transaction/${milestone.transactionId}`, '_blank')}
                        milestone={milestone}
                    />
                    <BuyerReviewModal
                        show={showBuyerReviewModal}
                        onClose={() => setShowBuyerReviewModal(false)}
                        onSave={(feedbackData) => handleFeedbackUpdate(feedbackData)}
                        formType={buyerRoles.includes(userRole) ? 'buyer' :
                            sellerRoles.includes(userRole) ? 'seller' : 'pm'}
                    />
                    <RaiseFlagModal
                        show={showRaiseFlagModal}
                        onClose={() => setShowRaiseFlagModal(false)}
                        onSend={handleRaiseFlag}
                        projectInfo={{
                            projectId: selectedGroup?.projectId || selectedGroup?.groupId,
                            projectName: selectedGroup?.name,
                            pmName: currentUser?.name
                        }}
                    />
                </div>
            </div >
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div >
    );
};

export default Communications;
